import type {
  ParentTuitionPaymentMethodSnapshot,
  ParentTuitionSnapshot,
  ParentTuitionStudentSnapshot,
  TuitionDocumentSnapshot,
  TuitionInstallmentSnapshot,
  TuitionPayerSnapshot,
  TuitionTransactionSnapshot,
} from "../shared/tuitionData";

const CHARGE_ENTRY_TYPES = new Set([
  "tuition",
  "charge",
  "registration_fee",
  "security_fee",
  "late_fee",
]);
const CREDIT_ENTRY_TYPES = new Set(["scholarship", "credit"]);

export type ParentTuitionDialogId =
  | "payment"
  | "statement"
  | "receipt"
  | "payors"
  | "methods";

export type ParentTuitionCheckoutRequest =
  | { kind: "full" }
  | { kind: "overdue" }
  | { kind: "custom"; amountCents: number }
  | { kind: "installment"; installmentId: string };

export type ParentTuitionDialogDocumentView = {
  id: string;
  label: string;
  detail: string;
  badgeLabel: string;
  document: TuitionDocumentSnapshot;
};

export type ParentTuitionPaymentMethodView = {
  id: string;
  label: string;
  detail: string;
  isDefault: boolean;
  isAutoPayMethod: boolean;
  isActive: boolean;
};

export type ParentTuitionPayerView = TuitionPayerSnapshot & {
  allocationLabel: string;
  balanceLabel: string;
};

export type ParentTuitionStudentView = ParentTuitionStudentSnapshot & {
  tuitionLabel: string;
  feesLabel: string;
  discountsLabel: string;
  subtotalLabel: string;
};

export type ParentTuitionInstallmentOptionView = {
  id: string;
  label: string;
  remainingAmountCents: number;
  disabled: boolean;
};

export type ParentTuitionInstallmentView = {
  installment: TuitionInstallmentSnapshot;
  amountLabel: string;
  remainingAmountLabel: string;
  statusLabel: string;
  statusDetail: string | null;
  statusTone: "success" | "warning" | "danger" | "muted";
};

export type ParentTuitionTransactionFilterKey =
  | "all"
  | "charge"
  | "payment"
  | "credit"
  | "late_fee"
  | "adjustment";

export type ParentTuitionTransactionView = {
  transaction: TuitionTransactionSnapshot;
  dateLabel: string;
  description: string;
  studentLabel: string;
  typeKey: ParentTuitionTransactionFilterKey;
  typeLabel: string;
  paymentStatusLabel: string;
  paymentStatusDetail: string | null;
  paymentStatusTone: "success" | "warning" | "danger" | "muted";
  amountLabel: string;
  amountTone: "default" | "positive";
  balanceLabel: string;
};

export type ParentTuitionViewModel = {
  academicYearLabel: string;
  academicYearValue: string;
  familyName: string | null;
  paymentMethodSummary: string;
  payInFullAmountCents: number;
  payInFullLabel: string;
  overdueAmountCents: number;
  overdueLabel: string;
  customAmountLabel: string;
  installmentPlanLabel: string;
  progress: {
    totalTuitionLabel: string;
    creditsAppliedLabel: string;
    totalPaidLabel: string;
    remainingBalanceLabel: string;
    progressPercentage: number;
    nextPaymentLabel: string;
    nextPaymentDetail: string;
    nextPaymentTone: "success" | "warning" | "danger";
    autoPayDetail: string;
  };
  billingProfile: {
    paymentMethodSummary: string;
    autoPayLabel: string;
    autoPayDetail: string;
  };
  paymentMethods: ParentTuitionPaymentMethodView[];
  payers: ParentTuitionPayerView[];
  students: ParentTuitionStudentView[];
  installmentOptions: ParentTuitionInstallmentOptionView[];
  installments: ParentTuitionInstallmentView[];
  transactions: ParentTuitionTransactionView[];
  transactionFilters: Array<{
    id: ParentTuitionTransactionFilterKey;
    label: string;
  }>;
  statementDocuments: ParentTuitionDialogDocumentView[];
  receiptDocuments: ParentTuitionDialogDocumentView[];
  primaryPaymentMethod: ParentTuitionPaymentMethodView | null;
};

const normalizeText = (value: unknown): string => String(value ?? "").trim();

export const formatCurrency = (cents: number): string =>
  `$${(Math.abs(cents) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

export const formatCurrencyWithSign = (cents: number): string =>
  cents < 0 ? `-${formatCurrency(cents)}` : formatCurrency(cents);

export const humanizeEntryType = (value: unknown): string => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return "Ledger entry";
  }
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
};

const resolveTransactionType = (
  transaction: TuitionTransactionSnapshot,
): ParentTuitionTransactionFilterKey => {
  if (transaction.entryType === "payment") {
    return "payment";
  }
  if (transaction.entryType === "refund") {
    return "adjustment";
  }
  if (transaction.entryType === "late_fee") {
    return "late_fee";
  }
  if (
    CREDIT_ENTRY_TYPES.has(transaction.entryType) ||
    transaction.amountCents < 0
  ) {
    return "credit";
  }
  if (CHARGE_ENTRY_TYPES.has(transaction.entryType)) {
    return "charge";
  }
  return "adjustment";
};

const resolveInstallmentStatusView = (
  installment: TuitionInstallmentSnapshot,
): Pick<
  ParentTuitionInstallmentView,
  "statusLabel" | "statusDetail" | "statusTone"
> => {
  if (installment.status === "paid") {
    return {
      statusLabel: "Paid",
      statusDetail: installment.dueDateLabel,
      statusTone: "success",
    };
  }

  if (installment.status === "overdue") {
    return {
      statusLabel: "Overdue",
      statusDetail: `${formatCurrency(installment.remainingAmountCents)} remaining`,
      statusTone: "danger",
    };
  }

  if (installment.status === "due") {
    return {
      statusLabel: "Due now",
      statusDetail: `${formatCurrency(installment.remainingAmountCents)} remaining`,
      statusTone: "warning",
    };
  }

  return {
    statusLabel: "Upcoming",
    statusDetail: installment.dueDateLabel,
    statusTone: "muted",
  };
};

const resolvePaymentStatus = (
  transaction: TuitionTransactionSnapshot,
): Pick<
  ParentTuitionTransactionView,
  "paymentStatusLabel" | "paymentStatusDetail" | "paymentStatusTone"
> => {
  const receiptReference =
    transaction.stripeCorrelation?.paymentIntentId ??
    transaction.stripeCorrelation?.providerEventId ??
    transaction.referenceCode;

  if (transaction.entryType === "payment" && transaction.isStripeCorrelated) {
    return {
      paymentStatusLabel: "Receipt ready",
      paymentStatusDetail: receiptReference
        ? `Ref ${receiptReference}`
        : "Stripe-confirmed payment",
      paymentStatusTone: "success",
    };
  }

  if (transaction.entryType === "refund" && transaction.isStripeCorrelated) {
    return {
      paymentStatusLabel: "Refund synced",
      paymentStatusDetail: receiptReference
        ? `Ref ${receiptReference}`
        : "Stripe-confirmed refund",
      paymentStatusTone: "danger",
    };
  }

  if (transaction.entryType === "payment") {
    return {
      paymentStatusLabel: "Recorded payment",
      paymentStatusDetail: transaction.referenceCode
        ? `Ref ${transaction.referenceCode}`
        : "Internal ledger entry",
      paymentStatusTone: "muted",
    };
  }

  if (transaction.isStripeCorrelated) {
    return {
      paymentStatusLabel: "Stripe-synced",
      paymentStatusDetail: receiptReference
        ? `Ref ${receiptReference}`
        : "Linked to Stripe activity",
      paymentStatusTone: "success",
    };
  }

  return {
    paymentStatusLabel: "Billed item",
    paymentStatusDetail: transaction.status
      ? humanizeEntryType(transaction.status)
      : "Ledger activity",
    paymentStatusTone: "muted",
  };
};

const buildDialogDocumentView = (
  document: TuitionDocumentSnapshot,
): ParentTuitionDialogDocumentView => ({
  id: document.id,
  label: document.name,
  detail: [document.category, document.studentName, document.uploadedAtLabel]
    .filter(Boolean)
    .join(" • "),
  badgeLabel:
    document.downloadMode === "remote"
      ? "Remote"
      : document.downloadMode === "derived"
        ? "Generated"
        : "Unavailable",
  document,
});

const buildPaymentMethodView = (
  method: ParentTuitionPaymentMethodSnapshot,
  autoPayEnrolled: boolean,
  primaryMethodId: string | null,
): ParentTuitionPaymentMethodView => {
  const typeLabel =
    method.methodType === "bank_account"
      ? "Bank account"
      : method.methodType === "card"
        ? "Card"
        : "Payment method";
  const provider = method.provider || method.label || typeLabel;
  const lastFour = method.lastFour ? `ending in ${method.lastFour}` : typeLabel;

  return {
    id: method.id,
    label: `${provider} ${lastFour}`.trim(),
    detail: [
      humanizeEntryType(method.methodType),
      method.status ? humanizeEntryType(method.status) : "Active",
    ].join(" • "),
    isDefault: method.isPreferred,
    isAutoPayMethod: autoPayEnrolled && method.id === primaryMethodId,
    isActive: normalizeText(method.status || "active") !== "inactive",
  };
};

const buildStudentView = (
  student: ParentTuitionStudentSnapshot,
): ParentTuitionStudentView => {
  const tuitionTotalCents = student.breakdown
    .filter((line) => /tuition/i.test(line.label))
    .reduce((sum, line) => sum + line.amountCents, 0);
  const feeTotalCents = student.breakdown
    .filter((line) => line.tone === "charge" && !/tuition/i.test(line.label))
    .reduce((sum, line) => sum + line.amountCents, 0);
  const discountsCents = student.breakdown
    .filter((line) => line.tone !== "charge")
    .reduce((sum, line) => sum + Math.abs(line.amountCents), 0);

  return {
    ...student,
    tuitionLabel: formatCurrency(tuitionTotalCents),
    feesLabel: formatCurrency(feeTotalCents),
    discountsLabel:
      discountsCents > 0 ? `-${formatCurrency(discountsCents)}` : "$0",
    subtotalLabel: formatCurrency(student.subtotalCents),
  };
};

export const buildParentTuitionViewModel = (
  snapshot: ParentTuitionSnapshot,
): ParentTuitionViewModel => {
  const paymentTotalsCents = snapshot.transactions.reduce(
    (sum, transaction) => {
      if (transaction.entryType !== "payment") {
        return sum;
      }
      return sum + Math.abs(Math.min(transaction.amountCents, 0));
    },
    0,
  );
  const creditTotalsCents = Math.max(
    snapshot.totalCreditsCents - paymentTotalsCents,
    0,
  );
  const progressDenominator = Math.max(
    snapshot.totalChargesCents - creditTotalsCents,
    0,
  );
  const progressPercentage =
    progressDenominator <= 0
      ? 100
      : Math.max(
          0,
          Math.min(
            100,
            Math.round((paymentTotalsCents / progressDenominator) * 100),
          ),
        );
  const nextOpenInstallment =
    snapshot.installmentSchedule.find(
      (installment) => installment.remainingAmountCents > 0,
    ) ?? null;
  const overdueInstallment =
    snapshot.installmentSchedule.find(
      (installment) => installment.status === "overdue",
    ) ?? null;
  const primaryMethodId =
    snapshot.paymentMethods.find((method) => method.isPreferred)?.id ??
    snapshot.paymentMethods[0]?.id ??
    null;
  const paymentMethods = snapshot.paymentMethods.map((method) =>
    buildPaymentMethodView(method, snapshot.autoPayEnrolled, primaryMethodId),
  );
  const primaryPaymentMethod =
    paymentMethods.find((method) => method.id === primaryMethodId) ?? null;
  const studentNameById = new Map(
    snapshot.students.map((student) => [student.id, student.name] as const),
  );

  let runningBalanceCents = snapshot.netBalanceCents;
  const transactions = snapshot.transactions.map((transaction) => {
    const paymentStatus = resolvePaymentStatus(transaction);
    const typeKey = resolveTransactionType(transaction);
    const view: ParentTuitionTransactionView = {
      transaction,
      dateLabel: transaction.observedDateLabel,
      description:
        normalizeText(transaction.description) ||
        humanizeEntryType(transaction.entryType),
      studentLabel: transaction.studentId
        ? (studentNameById.get(transaction.studentId) ?? "Family-level")
        : "Family-level",
      typeKey,
      typeLabel:
        typeKey === "late_fee" ? "Late fee" : humanizeEntryType(typeKey),
      paymentStatusLabel: paymentStatus.paymentStatusLabel,
      paymentStatusDetail: paymentStatus.paymentStatusDetail,
      paymentStatusTone: paymentStatus.paymentStatusTone,
      amountLabel: formatCurrencyWithSign(transaction.amountCents),
      amountTone: transaction.amountCents < 0 ? "positive" : "default",
      balanceLabel: formatCurrency(runningBalanceCents),
    };
    runningBalanceCents -= transaction.amountCents;
    return view;
  });

  const statementDocuments = snapshot.documents
    .filter(
      (document) =>
        document.kind === "statement" || document.kind === "contract",
    )
    .map((document) => buildDialogDocumentView(document));
  const receiptDocuments = snapshot.documents
    .filter((document) => document.kind === "receipt")
    .map((document) => buildDialogDocumentView(document));

  return {
    academicYearLabel: `Academic Year ${snapshot.academicYear}`,
    academicYearValue: snapshot.academicYear,
    familyName: snapshot.familyName,
    paymentMethodSummary:
      snapshot.paymentMethodSummary || "No payment method summary available",
    payInFullAmountCents: Math.max(snapshot.netBalanceCents, 0),
    payInFullLabel: formatCurrency(Math.max(snapshot.netBalanceCents, 0)),
    overdueAmountCents: Math.max(snapshot.overdueCents, 0),
    overdueLabel: formatCurrency(Math.max(snapshot.overdueCents, 0)),
    customAmountLabel: "Custom or pay-ahead amount",
    installmentPlanLabel:
      snapshot.installmentSchedule.length > 0
        ? `${snapshot.installmentSchedule.length}-item schedule`
        : "No schedule on file",
    progress: {
      totalTuitionLabel: formatCurrency(snapshot.totalChargesCents),
      creditsAppliedLabel: formatCurrency(creditTotalsCents),
      totalPaidLabel: formatCurrency(paymentTotalsCents),
      remainingBalanceLabel: formatCurrency(
        Math.max(snapshot.netBalanceCents, 0),
      ),
      progressPercentage,
      nextPaymentLabel: overdueInstallment
        ? `Overdue: ${formatCurrency(Math.max(snapshot.overdueCents, 0))}`
        : nextOpenInstallment
          ? `Next payment: ${formatCurrency(nextOpenInstallment.remainingAmountCents)}`
          : "All caught up",
      nextPaymentDetail: overdueInstallment
        ? `Past-due balance from ${overdueInstallment.dueDateLabel}`
        : nextOpenInstallment
          ? `${nextOpenInstallment.label} due ${nextOpenInstallment.dueDateLabel}`
          : "No open installment is currently due.",
      nextPaymentTone: overdueInstallment
        ? "danger"
        : nextOpenInstallment
          ? "warning"
          : "success",
      autoPayDetail: snapshot.autoPayEnrolled
        ? "Auto-pay is active on the current billing profile."
        : "Auto-pay is not active on the current billing profile.",
    },
    billingProfile: {
      paymentMethodSummary: snapshot.paymentMethodSummary,
      autoPayLabel: snapshot.autoPayEnrolled ? "Enabled" : "Not enabled",
      autoPayDetail: snapshot.autoPayEnrolled
        ? "Hosted billing management controls future auto-pay changes."
        : "Use hosted billing management to enroll a method in auto-pay.",
    },
    paymentMethods,
    payers: snapshot.payerHolders.map((payer) => ({
      ...payer,
      allocationLabel: `${payer.allocationPercent}%`,
      balanceLabel: formatCurrency(payer.allocationBalanceCents),
    })),
    students: snapshot.students.map((student) => buildStudentView(student)),
    installmentOptions: snapshot.installmentSchedule.map((installment) => ({
      id: installment.id,
      label: `${installment.label} • ${installment.dueDateLabel} • ${formatCurrency(installment.remainingAmountCents)}`,
      remainingAmountCents: installment.remainingAmountCents,
      disabled: installment.remainingAmountCents <= 0,
    })),
    installments: snapshot.installmentSchedule.map((installment) => ({
      installment,
      amountLabel: formatCurrency(installment.amountCents),
      remainingAmountLabel: formatCurrency(installment.remainingAmountCents),
      ...resolveInstallmentStatusView(installment),
    })),
    transactions,
    transactionFilters: [
      { id: "all", label: "All transactions" },
      { id: "charge", label: "Charges" },
      { id: "payment", label: "Payments" },
      { id: "credit", label: "Credits" },
      { id: "late_fee", label: "Late fees" },
      { id: "adjustment", label: "Adjustments" },
    ],
    statementDocuments,
    receiptDocuments,
    primaryPaymentMethod,
  };
};
